import { useEffect, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { CameraMenuIcon, ContactMenuIcon, FileMenuIcon, LocationMenuIcon, MicMenuIcon, PhoneMenuIcon, TimeMenuIcon } from './icons';
import { ICON_BTN } from './styles';
import styles from './AttachMenu.module.css';
import { IconButton, ScrollArea } from '../ui';

// The composer's "+" attachment panel — native's `ChatAttachmentMenu` (iOS
// `ChatComposerControls.swift`, Android `ChatComposer.kt`): a titled panel spanning the whole
// composer card and floating just above it, NOT a popover hanging off the "+" button. The rows are
// the attach options — "Choose file" (the file picker), "Contact" (the contact picker), "Location"
// (the place picker) and "Share a time" (the datetime picker), plus "Take Photo" where the browser
// can reach a camera. When no panel is needed the caller renders the bare button instead — this
// component is only used once there's more than one attach option.
//
// ⚠️ The panel is absolutely positioned against the COMPOSER CARD, not against this component: the
// wrapper is deliberately unpositioned so the card is the nearest positioned ancestor. Both call
// sites carry `position: relative` for that reason, and `attach-panel.test.ts` pins it — drop it and
// the panel silently escapes to the viewport.
export interface AttachMenuItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  onSelect: () => void;
}

export function AttachMenu({
  triggerLabel,
  title,
  items,
  triggerClassName,
}: {
  triggerLabel: string;
  /** The panel's heading — native's `chat.attach` row. */
  title: string;
  items: AttachMenuItem[];
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      // The panel is no longer a child of the trigger's wrapper's layout box in any visual sense,
      // but it IS still a DOM descendant of `rootRef` — so one containment check covers both, and a
      // press on a row never counts as a press outside.
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={styles.root}>
      <IconButton
        size="small"
        onClick={() => setOpen((v) => !v)}
        aria-label={triggerLabel}
        title={triggerLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        className={triggerClassName}
      >
        {/* Figma "Prompt Box Buttons" (14705:128186): every composer glyph is a 1.5px absolute stroke —
            this trigger used to carry the default relative 2.5, reading visibly heavier than its
            siblings. */}
        <Plus size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
      </IconButton>
      {open && (
        <div ref={menuRef} role="menu" aria-label={title} className={styles.menu}>
          <div className={styles.title}>{title}</div>
          {/* The 360 cap only bites on a short viewport; ScrollArea keeps the rows reachable when it
              does, and draws the DS bar rather than the OS one. */}
          <ScrollArea>
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className={styles.item}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
              >
                <span className={styles.itemIcon} aria-hidden>
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </button>
            ))}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}

// Re-export the option glyphs so the composer can hand them in without reaching into ./icons.
export { CameraMenuIcon, ContactMenuIcon, FileMenuIcon, LocationMenuIcon, MicMenuIcon, PhoneMenuIcon, TimeMenuIcon };
